import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AlertFlagsRequest, AlertFlagsResponse } from '../models/alert-flags-request.model';
import { CreateCareCallAlertsRequest, CreateCareCallAlertsResponse } from '../models/care-call-alert.model';
import { ApiResponse } from '../models/api-response.model';
import { FirestoreService } from './firestore.service';
import { AlertFlagResolutionConfigurationResp } from './models/alert-summary-navigation.model';

@Injectable({
  providedIn: 'root',
})
export class AlertFlagsService {
  baseUrl = `${environment.welbyEndpoint}/api/v1/core/alert-flags`;
  constructor(private http: HttpClient, private fs: FirestoreService) {}

  // Note: /getByClients does NOT use the ApiResponse<T> envelope — the controller returns the payload directly.
  getAlertsByClients(body: AlertFlagsRequest): Observable<AlertFlagsResponse> {
    const url = `${this.baseUrl}/getByClients`;
    return this.http.post<AlertFlagsResponse>(url, {
      isAdmin: body.isAdmin,
      providerId: body.providerId,
      clientsAccounts: body.clientsAccounts,
      page: body.page,
      pageSize: body.pageSize,
      sortBy: body.sortBy,
      sortOrder: body.sortOrder,
    });
  }

  getNumberOfAlerts(body: Pick<AlertFlagsRequest, 'isAdmin' | 'clientsAccounts' | 'page' | 'pageSize' | 'providerId'>): Observable<number> {
    const url = `${this.baseUrl}/getNumberOfAlerts`;
    return this.http.post<number>(url, {
      isAdmin: body.isAdmin,
      clientsAccounts: body.clientsAccounts,
      providerId: body.providerId,
    });
  }

  // Returns the subset of patientIds that have an open Tier 3 vital alert. This endpoint uses the
  // standardized ApiResponse<T> envelope, so we unwrap .data here (never in the component).
  getOpenTier3PatientIds(patientIds: string[]): Observable<string[]> {
    const url = `${this.baseUrl}/open-tier3-by-patients`;
    return this.http
      .post<ApiResponse<{ patientIds: string[] }>>(url, { patientIds })
      .pipe(map((res) => res?.data?.patientIds ?? []));
  }

  /**
   * BRD-CLIN-2026-006 §4.1 — POSTs to `/core/alert-flags/care-call` (welby-micro-service #957),
   * which writes the Care Call Guide's routable findings as `vital_flags` alerts.
   *
   * **NO CALLER. The Care Call Guide stopped writing alerts** — `vital_flags` is the record of a
   * patient's device READINGS, and a finding an MA documents on a call is not a reading, so the
   * guide now creates escalation TASKS instead (`care-call-guide-base.component.ts`). This method
   * and the endpoint behind it are the write surface for a path nothing takes any more. Kept rather
   * than deleted because retiring it also retires a live microservice endpoint, which is a decision
   * for that repo and not a side effect of this component's fix.
   *
   * Uses the standardized ApiResponse<T> envelope, so `.data` is unwrapped HERE and never in the
   * component (LEDGER 5.7). A response missing `data` is normalized to a zero-write result rather
   * than surfacing `undefined` to the caller's success snackbar.
   */
  createCareCallAlerts(body: CreateCareCallAlertsRequest): Observable<CreateCareCallAlertsResponse> {
    const url = `${this.baseUrl}/care-call`;
    return this.http
      .post<ApiResponse<CreateCareCallAlertsResponse>>(url, body)
      .pipe(map((res) => res?.data ?? { created: 0, alert_ids: [] }));
  }

  getAlertFlagConfiguration(): Promise<firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>> {
    return this.fs
      .col<AlertFlagResolutionConfigurationResp>('welby_configuration')
      .doc('flag_resolution')
      .get()
      .pipe(take(1))
      .toPromise();
  }
}
